import Events from '@clearkit/icons/glyphs/Event';
import Filter from '@clearkit/icons/glyphs/Filter';
import FilterActive from '@clearkit/icons/glyphs/FilterActive';
import Pageview from '@clearkit/icons/glyphs/View';
import {
  CKActionMenu,
  CKButton,
  CKCheckbox,
  CKPopover,
  CKTab,
  CKTabList,
  CKTabs,
} from '@clearkit/react';
import { NetworkStatus } from 'apollo-client';
import { useQuery } from 'react-apollo';

import GraphqlError from '~/components/GraphqlError';
import SearchTextField from '~/components/SearchTextField';
import Select from '~/components/Select';
import EventsList from '~/components/Sidebar/RedesignedEventsList';
import PageviewsList from '~/components/Sidebar/RedesignedPageviewsList';
import { paginateData } from '~/lib/graphql/pagination';

import query from './query.graphql';

const initialPageSize = 40;
const perPage = 20;

const CompanyActivity = ({ company, className }) => {
  const [type, setType] = useState();
  const [path, setPath] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [excludingAnonymous, setExcludingAnonymous] = useState(false);

  const variables = {
    first: initialPageSize,
    pageviewsFirst: initialPageSize,
    id: company.id,
    excludingAnonymous: excludingAnonymous,
  };

  if (path) variables.pageviewsPath = path;
  variables.type = type || undefined;

  const {
    data = {},
    error,
    fetchMore,
    loading,
    networkStatus,
    refetch,
  } = useQuery(query, {
    fetchPolicy: 'cache-and-network',
    variables,
    notifyOnNetworkStatusChange: true,
  });

  company = { ...company, ...data?.company };

  if (error) {
    return <GraphqlError error={error} loading={loading} refetch={refetch} />;
  }

  const {
    events,
    eventsHasNextPage,
    eventsEndCursor,
    pageviews,
    pageviewsHasNextPage,
    pageviewsEndCursor,
  } = paginateData(company, {
    fields: ['events', 'pageviews'],
  });

  const { eventTypes = [] } = data;

  const firstPageLoading = loading && networkStatus !== NetworkStatus.fetchMore;

  return (
    <div className={className}>
      <div className="z-10 flex flex-row justify-between px-8 py-6 border-b border-gray-200 align-center bg-gray-0">
        <CKTabs isSegmented index={tabIndex} onChange={setTabIndex}>
          <CKTabList aria-label="My tabs">
            <CKTab label="Pageviews" id="page" icon={<Pageview />}>
              Pageviews
            </CKTab>
            <CKTab label="Events" id="events" icon={<Events />}>
              Events
            </CKTab>
          </CKTabList>
        </CKTabs>

        {tabIndex === 0 && (
          <>
            <SearchTextField
              placeholder="Search by path..."
              className="flex-grow w-6/12 max-w-md ml-auto mr-4"
              onSearch={(e) => {
                let value = e.target.value;
                if (value && !value.startsWith('/')) {
                  value = `/${value}`;
                }
                setPath(value);
              }}
              value={path}
            />
            <CKPopover
              options={{
                gutter: 6,
                placement: 'bottom-start',
              }}
              popover={
                <CKActionMenu visible className="z-10">
                  <CKActionMenu.Item
                    icon={
                      <CKCheckbox state={excludingAnonymous} className="pt-1" />
                    }
                    onClick={() => {
                      setExcludingAnonymous(!excludingAnonymous);
                    }}>
                    Hide anonymous pageviews
                  </CKActionMenu.Item>
                </CKActionMenu>
              }>
              <CKButton>
                {excludingAnonymous ? <FilterActive /> : <Filter />}
              </CKButton>
            </CKPopover>
          </>
        )}

        {tabIndex === 1 && (
          <Select
            className="flex-grow w-6/12 max-w-md"
            options={['', ...eventTypes]}
            getOptionValue={(option) => option || 'All'}
            getOptionLabel={(option) => option || 'All events'}
            placeholder="Filter by event type"
            onChange={(value) => setType(value)}
            isDisabled={loading}
            value={[type || 'All events']}
          />
        )}
      </div>

      <div className="flex flex-col flex-grow">
        {tabIndex === 0 && (
          <PageviewsList
            className="px-8"
            events={pageviews}
            eventTypes={eventTypes}
            hasNextPage={pageviewsHasNextPage}
            id={company.id}
            path={path}
            loading={firstPageLoading}
            loadingMore={loading}
            onFilter={(path) => setPath(path)}
            onLoadMore={() => {
              if (loading) {
                return;
              }

              fetchMore({
                variables: {
                  ...variables,
                  pageviewsAfter: pageviewsEndCursor,
                  pageviewsFirst: perPage,
                },

                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) {
                    return prev;
                  }

                  fetchMoreResult.company.pageviews.edges = [
                    ...prev.company.pageviews.edges,
                    ...fetchMoreResult.company.pageviews.edges,
                  ];

                  return fetchMoreResult;
                },
              });
            }}
          />
        )}
        {tabIndex === 1 && (
          <EventsList
            className="flex flex-col flex-grow px-4"
            events={events}
            hasNextPage={eventsHasNextPage}
            type={type}
            id={company.id}
            loading={firstPageLoading}
            loadingMore={loading}
            onFilter={(type) => setType(type)}
            onLoadMore={() => {
              if (loading) {
                return;
              }

              fetchMore({
                variables: {
                  ...variables,
                  eventsFirst: eventsEndCursor,
                  eventsAfter: perPage,
                },

                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) {
                    return prev;
                  }

                  fetchMoreResult.company.events.edges = [
                    ...prev.company.events.edges,
                    ...fetchMoreResult.company.events.edges,
                  ];

                  return fetchMoreResult;
                },
              });
            }}
          />
        )}
      </div>
    </div>
  );
};

CompanyActivity.propTypes = {
  company: PropTypes.object.isRequired,
  className: PropTypes.string,
};

export default CompanyActivity;
